/**
 * NetflixProgressBar Component
 * Netflix-style progress bar with:
 * - Draggable handle
 * - Buffer indicator (gray shading)
 * - Hover tooltip with thumbnail preview
 * - Live preview during drag with debounce
 */

import { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import type { Photo } from '@/types';
import styles from './NetflixProgressBar.module.css';

interface NetflixProgressBarProps {
  photos: Photo[];
  currentIndex: number;
  bufferedRanges: Array<{ start: number; end: number }>;
  onSeek: (index: number) => void;
  onSeekStart?: () => void;
  onSeekEnd?: () => void;
  disabled?: boolean;
  timezone?: string;
  // Hover thumbnail props
  hoverThumbCache?: Map<number, string>; // index -> blob URL
  hoverThumbInterval?: number; // Show thumb every N frames (default: 10)
  /** Current playback fps used to compute the remaining-time label. */
  playbackFps?: number;
}

/** Format a time delta in seconds as Netflix-style negative timer ("-12:34"). */
function formatRemaining(seconds: number): string {
  const total = Math.max(0, Math.round(seconds));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n: number) => n.toString().padStart(2, "0");
  return h > 0
    ? `-${h}:${pad(m)}:${pad(s)}`
    : `-${pad(m)}:${pad(s)}`;
}

const DEBOUNCE_MS = 120;

/**
 * Format timestamp for tooltip
 */
function formatTimestamp(
  timestamp: { toDate?: () => Date } | Date | null | undefined,
  timezone?: string
): string {
  if (!timestamp) return '';
  try {
    const date = 'toDate' in timestamp && timestamp.toDate
      ? timestamp.toDate()
      : new Date(timestamp as unknown as string);
    return date.toLocaleString('it-IT', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: timezone,
    });
  } catch {
    return '';
  }
}

export function NetflixProgressBar({
  photos,
  currentIndex,
  bufferedRanges,
  onSeek,
  onSeekStart,
  onSeekEnd,
  disabled = false,
  timezone,
  hoverThumbCache,
  hoverThumbInterval = 10,
  playbackFps,
}: NetflixProgressBarProps) {
  const trackRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);
  const [hoverPosition, setHoverPosition] = useState(0);
  const [trackWidth, setTrackWidth] = useState(0);
  const [tooltipWidth, setTooltipWidth] = useState(0);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSeekIndexRef = useRef<number>(currentIndex);

  // While dragging, paint the handle from the live drag index so it tracks
  // the finger/cursor with zero lag — the actual seek (which loads images)
  // is still debounced below.
  const displayIndex = isDragging && dragIndex !== null ? dragIndex : currentIndex;
  const progressPercent = photos.length > 1
    ? (displayIndex / (photos.length - 1)) * 100
    : 0;

  // Calculate buffered ranges as CSS gradient stops
  const bufferGradient = useMemo(() => {
    if (bufferedRanges.length === 0 || photos.length <= 1) {
      return 'transparent';
    }

    const stops: string[] = [];
    const total = photos.length - 1;

    // Sort ranges by start
    const sortedRanges = [...bufferedRanges].sort((a, b) => a.start - b.start);

    let lastEnd = 0;
    for (const range of sortedRanges) {
      const startPct = (range.start / total) * 100;
      const endPct = (range.end / total) * 100;

      // Gap before this range (unbuffered)
      if (startPct > lastEnd) {
        stops.push(`rgba(255,255,255,0.2) ${lastEnd}%`);
        stops.push(`rgba(255,255,255,0.2) ${startPct}%`);
      }

      // Buffered range
      stops.push(`rgba(255,255,255,0.4) ${startPct}%`);
      stops.push(`rgba(255,255,255,0.4) ${endPct}%`);

      lastEnd = endPct;
    }

    // Remaining unbuffered
    if (lastEnd < 100) {
      stops.push(`rgba(255,255,255,0.2) ${lastEnd}%`);
      stops.push(`rgba(255,255,255,0.2) 100%`);
    }

    return `linear-gradient(to right, ${stops.join(', ')})`;
  }, [bufferedRanges, photos.length]);

  // Get index from mouse/touch position
  const getIndexFromPosition = useCallback((clientX: number): number => {
    if (!trackRef.current || photos.length === 0) return 0;

    const rect = trackRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const percent = Math.max(0, Math.min(1, x / rect.width));
    return Math.round(percent * (photos.length - 1));
  }, [photos.length]);

  // Debounced seek
  const debouncedSeek = useCallback((index: number) => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      if (index !== lastSeekIndexRef.current) {
        lastSeekIndexRef.current = index;
        onSeek(index);
      }
    }, DEBOUNCE_MS);
  }, [onSeek]);

  // Immediate seek (for final position)
  const immediateSeek = useCallback((index: number) => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    if (index !== lastSeekIndexRef.current) {
      lastSeekIndexRef.current = index;
      onSeek(index);
    }
  }, [onSeek]);

  // Handle mouse down on track
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (disabled) return;
    e.preventDefault();

    setIsDragging(true);
    onSeekStart?.();

    const index = getIndexFromPosition(e.clientX);
    setDragIndex(index);
    immediateSeek(index);
  }, [disabled, getIndexFromPosition, immediateSeek, onSeekStart]);

  // Handle touch start on track (mobile — avoids the 300ms click delay)
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (disabled) return;
    const t = e.touches[0];
    if (!t || !trackRef.current) return;

    setIsDragging(true);
    onSeekStart?.();

    const rect = trackRef.current.getBoundingClientRect();
    const x = t.clientX - rect.left;
    const percent = Math.max(0, Math.min(1, x / rect.width));
    const index = Math.round(percent * (photos.length - 1));

    setDragIndex(index);
    setHoverIndex(index);
    setHoverPosition(x);
    immediateSeek(index);
  }, [disabled, photos.length, immediateSeek, onSeekStart]);

  // Handle mouse move (drag + hover)
  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!trackRef.current) return;

    const rect = trackRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percent = Math.max(0, Math.min(1, x / rect.width));
    const index = Math.round(percent * (photos.length - 1));

    // Update hover state
    setHoverIndex(index);
    setHoverPosition(x);

    // If dragging, paint handle live + seek with debounce
    if (isDragging) {
      setDragIndex(index);
      debouncedSeek(index);
    }
  }, [isDragging, photos.length, debouncedSeek]);

  // Handle mouse up
  const handleMouseUp = useCallback(() => {
    if (isDragging) {
      const finalIndex = dragIndex;
      setIsDragging(false);
      setDragIndex(null);
      if (finalIndex !== null) immediateSeek(finalIndex);
      onSeekEnd?.();
    }
  }, [isDragging, dragIndex, immediateSeek, onSeekEnd]);

  // Handle mouse leave
  const handleMouseLeave = useCallback(() => {
    setHoverIndex(null);
    if (isDragging) {
      // Don't end drag on leave - will be handled by global mouseup
    }
  }, [isDragging]);

  // Global mouse/touch up listeners for drag end outside component
  useEffect(() => {
    if (!isDragging) return;

    const endDrag = (finalIndex: number | null) => {
      setIsDragging(false);
      setDragIndex(null);
      // Hide the hover tooltip when the drag ends (touch has no hover state).
      setHoverIndex(null);
      if (finalIndex !== null) immediateSeek(finalIndex);
      onSeekEnd?.();
    };

    const handleGlobalMouseUp = () => {
      endDrag(dragIndex);
    };

    const handleGlobalMouseMove = (e: MouseEvent) => {
      const index = getIndexFromPosition(e.clientX);
      setDragIndex(index);
      debouncedSeek(index);
    };

    const handleGlobalTouchMove = (e: TouchEvent) => {
      const t = e.touches[0];
      if (!t || !trackRef.current) return;
      // Prevent the page from scrolling while dragging on mobile.
      if (e.cancelable) e.preventDefault();
      const rect = trackRef.current.getBoundingClientRect();
      const x = t.clientX - rect.left;
      const percent = Math.max(0, Math.min(1, x / rect.width));
      const index = Math.round(percent * (photos.length - 1));
      setDragIndex(index);
      // Keep the hover tooltip in sync with the finger so the thumbnail
      // preview shows during touch drags.
      setHoverIndex(index);
      setHoverPosition(x);
      debouncedSeek(index);
    };

    const handleGlobalTouchEnd = () => {
      endDrag(dragIndex);
    };

    window.addEventListener('mouseup', handleGlobalMouseUp);
    window.addEventListener('mousemove', handleGlobalMouseMove);
    // Touch move must be non-passive so we can preventDefault page scrolling.
    window.addEventListener('touchmove', handleGlobalTouchMove, { passive: false });
    window.addEventListener('touchend', handleGlobalTouchEnd);
    window.addEventListener('touchcancel', handleGlobalTouchEnd);

    return () => {
      window.removeEventListener('mouseup', handleGlobalMouseUp);
      window.removeEventListener('mousemove', handleGlobalMouseMove);
      window.removeEventListener('touchmove', handleGlobalTouchMove);
      window.removeEventListener('touchend', handleGlobalTouchEnd);
      window.removeEventListener('touchcancel', handleGlobalTouchEnd);
    };
  }, [isDragging, dragIndex, photos.length, getIndexFromPosition, debouncedSeek, immediateSeek, onSeekEnd]);

  // Cleanup debounce timer
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // Update lastSeekIndexRef when currentIndex changes externally
  useEffect(() => {
    if (!isDragging) {
      lastSeekIndexRef.current = currentIndex;
    }
  }, [currentIndex, isDragging]);

  // Track the track's width so we can clamp the hover tooltip to its bounds
  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    const update = () => setTrackWidth(el.getBoundingClientRect().width);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Measure the tooltip width when it appears so we can keep it inside bounds
  useEffect(() => {
    if (hoverIndex === null) return;
    const el = tooltipRef.current;
    if (!el) return;
    setTooltipWidth(el.getBoundingClientRect().width);
  }, [hoverIndex]);

  // Get hover thumbnail - find nearest cached index dynamically
  // This ensures thumbs show during progressive loading (granularity 100, 50, 25, 10)
  const hoverThumb = useMemo(() => {
    if (hoverIndex === null || !hoverThumbCache || hoverThumbCache.size === 0) return null;

    // First try exact interval match (fastest path)
    const intervalIndex = Math.round(hoverIndex / hoverThumbInterval) * hoverThumbInterval;
    if (hoverThumbCache.has(intervalIndex)) {
      return hoverThumbCache.get(intervalIndex)!;
    }

    // Fallback: find nearest cached index (for progressive loading)
    let nearestIndex = 0;
    let minDistance = Infinity;

    for (const cachedIndex of hoverThumbCache.keys()) {
      const distance = Math.abs(cachedIndex - hoverIndex);
      if (distance < minDistance) {
        minDistance = distance;
        nearestIndex = cachedIndex;
      }
    }

    return hoverThumbCache.get(nearestIndex) || null;
  }, [hoverIndex, hoverThumbCache, hoverThumbInterval]);

  // Hover photo for timestamp
  const hoverPhoto = hoverIndex !== null ? photos[hoverIndex] : null;

  return (
    <div
      className={`${styles.progressContainer} ${disabled ? styles.disabled : ''}`}
    >
      {/* Hover tooltip — clamped within track bounds (Netflix-style) */}
      {hoverIndex !== null && !disabled && (
        <div
          ref={tooltipRef}
          className={styles.tooltip}
          style={(() => {
            // Default placement: centered on the cursor
            if (!tooltipWidth || !trackWidth) {
              return { left: hoverPosition };
            }
            const half = tooltipWidth / 2;
            // Centered position; clamp so the whole tooltip stays in [0, trackWidth]
            const clamped = Math.max(half, Math.min(hoverPosition, trackWidth - half));
            return { left: clamped };
          })()}
        >
          {hoverThumb && (
            <img
              src={hoverThumb}
              alt=""
              className={styles.tooltipThumb}
              draggable={false}
            />
          )}
          <span className={styles.tooltipTime}>
            {formatTimestamp(hoverPhoto?.capturedAt, timezone)}
          </span>
        </div>
      )}

      <div className={styles.trackRow}>
        {/* Track wrapper - larger hit area for easier hover */}
        <div
          ref={trackRef}
          className={styles.trackWrapper}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseLeave}
          onTouchStart={handleTouchStart}
        >
          {/* Track */}
          <div className={`${styles.track} ${isDragging ? styles.dragging : ''}`}>
            {/* Buffer indicator */}
            <div
              className={styles.buffer}
              style={{ background: bufferGradient }}
            />

            {/* Progress fill */}
            <div
              className={styles.progress}
              style={{ width: `${progressPercent}%` }}
            />

            {/* Handle */}
            <div
              className={styles.handle}
              style={{ left: `${progressPercent}%` }}
            />
          </div>
        </div>

        {/* Remaining time (Netflix-style "-mm:ss") */}
        {photos.length > 1 && (
          <span className={styles.remaining}>
            {(() => {
              const fps = playbackFps && playbackFps > 0 ? playbackFps : 24;
              const framesLeft = Math.max(0, photos.length - 1 - currentIndex);
              return formatRemaining(framesLeft / fps);
            })()}
          </span>
        )}
      </div>
    </div>
  );
}
